import { useState, useEffect, useRef } from 'react';
import Header from './components/Header';
import GoalsColumn from './components/GoalsColumn';
import StepsColumn from './components/StepsColumn';
import TasksColumn from './components/TasksColumn';
import InitiativesColumn from './components/InitiativesColumn';
import logo from './logo.png';

export default function TaskManager() {
  const [goals, setGoals] = useState(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      const storedGoals = localStorage.getItem('goals');
      return storedGoals ? JSON.parse(storedGoals) : [];
    }
    return [];
  });

  const [newGoalText, setNewGoalText] = useState('');
  const [newStepText, setNewStepText] = useState('');
  const [newTaskText, setNewTaskText] = useState('');
  const [newInitiativeText, setNewInitiativeText] = useState('');
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [selectedStep, setSelectedStep] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [openSubmenuId, setOpenSubmenuId] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [editingItemId, setEditingItemId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [filterState, setFilterState] = useState('all');
  const fileInputRef = useRef(null);

  const [columnHeaders, setColumnHeaders] = useState(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      const storedHeaders = localStorage.getItem('columnHeaders');
      return storedHeaders ? JSON.parse(storedHeaders) : {
        goals: "Big Goals",
        steps: "SubGoals-Milestones",
        tasks: "Outcome Targets",
        initiatives: "Initiatives-Tasks"
      };
    }
    return {
      goals: "Big Goals",
      steps: "SubGoals-Milestones",
      tasks: "Outcome Targets",
      initiatives: "Initiatives-Tasks"
    };
  });

  // Save to local storage whenever goals change
  useEffect(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.setItem('goals', JSON.stringify(goals));
    }
  }, [goals]);

  // Load selections from local storage if available
  useEffect(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      const storedSelections = localStorage.getItem('selections');
      if (storedSelections) {
        const { goalId, stepId, taskId } = JSON.parse(storedSelections);
        setSelectedGoal(goalId);
        setSelectedStep(stepId);
        setSelectedTask(taskId);
      }
    }
  }, []);

  // Save selections to local storage
  useEffect(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.setItem('selections', JSON.stringify({
        goalId: selectedGoal,
        stepId: selectedStep,
        taskId: selectedTask
      }));
    }
  }, [selectedGoal, selectedStep, selectedTask]);

  // Close menus when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (!event.target.closest('.menu-container')) {
        setOpenMenuId(null);
        setOpenSubmenuId(null);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Save column headers to local storage
  useEffect(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.setItem('columnHeaders', JSON.stringify(columnHeaders));
    }
  }, [columnHeaders]);

  const downloadData = () => {
    // Prompt user for custom filename
    const customName = prompt('Enter a name for your download:', '');
    
    // If user cancels, don't proceed
    if (customName === null) return;
    
    // Get current date for the filename
    const now = new Date();
    const day = now.getDate();
    const month = now.toLocaleString('en-US', { month: 'long' });
    const year = now.getFullYear();
    const dateStr = `${day} ${month} ${year}`;
    
    // Create filename with custom name and date
    const sanitizedName = (customName.trim() || 'My Planning').replace(/[^a-zA-Z0-9\s-_]/g, '');
    const filename = `${sanitizedName}_${dateStr}.json`;
    
    const dataToDownload = {
      goals: goals,
      columnHeaders: columnHeaders
    };
    const jsonString = JSON.stringify(dataToDownload, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsedData = JSON.parse(e.target.result);
        if (parsedData.goals && Array.isArray(parsedData.goals)) {
          setGoals(parsedData.goals);
          if (parsedData.columnHeaders) {
            setColumnHeaders(parsedData.columnHeaders);
          }
          setSelectedGoal(null);
          setSelectedStep(null);
          setSelectedTask(null);
          alert('Data imported successfully!');
        } else {
          alert('Invalid data format. Please upload a valid task manager data file.');
        }
      } catch (error) {
        console.error('Failed to parse JSON:', error);
        alert('Failed to parse file. Please ensure it is a valid JSON file.');
      }
    };
    reader.readAsText(file);
    event.target.value = null;
  };

  const triggerFileUpload = () => {
    fileInputRef.current.click();
  };

  const clearAllData = () => {
    setGoals([]);
    setSelectedGoal(null);
    setSelectedStep(null);
    setSelectedTask(null);
    setColumnHeaders({
      goals: "Big Goals",
      steps: "SubGoals-Milestones",
      tasks: "Outcome Targets",
      initiatives: "Initiatives-Tasks"
    });
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.removeItem('goals');
      localStorage.removeItem('selections');
      localStorage.removeItem('columnHeaders');
    }
  };

  const formatIndentedData = () => {
    let output = '';
    goals.forEach(goal => {
      output += `${goal.title}${goal.completed ? ' (completed)' : ''}${goal.priority ? ` [${goal.priority}]` : ''}\n`;
      goal.steps.forEach(step => {
        output += `    ${step.title}${step.completed ? ' (completed)' : ''}${step.priority ? ` [${step.priority}]` : ''}\n`;
        step.tasks.forEach(task => {
          output += `        ${task.title}${task.completed ? ' (completed)' : ''}${task.priority ? ` [${task.priority}]` : ''}\n`;
          if (task.initiatives) {
            task.initiatives.forEach(initiative => {
              output += `            ${initiative.title}${initiative.completed ? ' (completed)' : ''}${initiative.priority ? ` [${initiative.priority}]` : ''}\n`;
            });
          }
        });
      });
      output += '\n';
    });
    return output;
  };

  const copyIndentedData = () => {
    const formattedData = formatIndentedData();
    navigator.clipboard.writeText(formattedData)
      .then(() => {
        alert('Data copied to clipboard!');
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
      });
  };

  // Drag and drop handlers
  const handleDragStart = (item, type) => {
    setDraggedItem({ ...item, type });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (targetItem, type) => {
    if (!draggedItem || draggedItem.type !== type) return;

    const reorderItems = (fromId, toId, items, setItems) => {
      const fromIndex = items.findIndex(item => item.id === fromId);
      const toIndex = items.findIndex(item => item.id === toId);
      
      if (fromIndex !== -1 && toIndex !== -1) {
        const newItems = [...items];
        const [movedItem] = newItems.splice(fromIndex, 1);
        newItems.splice(toIndex, 0, movedItem);
        setItems(newItems);
      }
    };

    if (type === 'goal') {
      reorderItems(draggedItem.id, targetItem.id, goals, setGoals);
    } else if (type === 'step' && selectedGoal) {
      const goalIndex = goals.findIndex(g => g.id === selectedGoal);
      if (goalIndex !== -1) {
        const newGoals = [...goals];
        reorderItems(draggedItem.id, targetItem.id, newGoals[goalIndex].steps, (newSteps) => {
          newGoals[goalIndex].steps = newSteps;
          setGoals(newGoals);
        });
      }
    } else if (type === 'task' && selectedGoal && selectedStep) {
      const goalIndex = goals.findIndex(g => g.id === selectedGoal);
      if (goalIndex !== -1) {
        const stepIndex = goals[goalIndex].steps.findIndex(s => s.id === selectedStep);
        if (stepIndex !== -1) {
          const newGoals = [...goals];
          reorderItems(draggedItem.id, targetItem.id, newGoals[goalIndex].steps[stepIndex].tasks, (newTasks) => {
            newGoals[goalIndex].steps[stepIndex].tasks = newTasks;
            setGoals(newGoals);
          });
        }
      }
    } else if (type === 'initiative' && selectedGoal && selectedStep && selectedTask) {
      const goalIndex = goals.findIndex(g => g.id === selectedGoal);
      if (goalIndex !== -1) {
        const stepIndex = goals[goalIndex].steps.findIndex(s => s.id === selectedStep);
        if (stepIndex !== -1) {
          const taskIndex = goals[goalIndex].steps[stepIndex].tasks.findIndex(t => t.id === selectedTask);
          if (taskIndex !== -1) {
            const newGoals = [...goals];
            const task = newGoals[goalIndex].steps[stepIndex].tasks[taskIndex];
            const initiatives = [...(task.initiatives || [])];
            const fromIndex = initiatives.findIndex(i => i.id === draggedItem.id);
            const toIndex = initiatives.findIndex(i => i.id === targetItem.id);
            
            if (fromIndex !== -1 && toIndex !== -1) {
              const [movedItem] = initiatives.splice(fromIndex, 1);
              initiatives.splice(toIndex, 0, movedItem);
              newGoals[goalIndex].steps[stepIndex].tasks[taskIndex].initiatives = initiatives;
              setGoals(newGoals);
            }
          }
        }
      }
    }
    setDraggedItem(null);
  };

  // Item management functions
  const addGoal = () => {
    if (newGoalText.trim()) {
      setGoals([...goals, {
        id: Date.now(),
        title: newGoalText,
        completed: false,
        priority: null,
        steps: [],
      }]);
      setNewGoalText('');
    }
  };

  const addStep = () => {
    if (newStepText.trim() && selectedGoal) {
      const updatedGoals = goals.map((goal) => {
        if (goal.id === selectedGoal) {
          return {
            ...goal,
            steps: [...goal.steps, {
              id: Date.now(),
              title: newStepText,
              completed: false,
              priority: null,
              tasks: [],
            }],
          };
        }
        return goal;
      });
      setGoals(updatedGoals);
      setNewStepText('');
    }
  };

  const addTask = () => {
    if (newTaskText.trim() && selectedGoal && selectedStep) {
      const updatedGoals = goals.map((goal) => {
        if (goal.id === selectedGoal) {
          return {
            ...goal,
            steps: goal.steps.map((step) => {
              if (step.id === selectedStep) {
                return {
                  ...step,
                  tasks: [...step.tasks, {
                    id: Date.now(),
                    title: newTaskText,
                    completed: false,
                    priority: null,
                    initiatives: [],
                  }],
                };
              }
              return step;
            }),
          };
        }
        return goal;
      });
      setGoals(updatedGoals);
      setNewTaskText('');
    }
  };

  const addInitiative = () => {
    if (newInitiativeText.trim() && selectedGoal && selectedStep && selectedTask) {
      const updatedGoals = goals.map((goal) => {
        if (goal.id === selectedGoal) {
          return {
            ...goal,
            steps: goal.steps.map((step) => {
              if (step.id === selectedStep) {
                return {
                  ...step,
                  tasks: step.tasks.map((task) => {
                    if (task.id === selectedTask) {
                      return {
                        ...task,
                        initiatives: [...(task.initiatives || []), {
                          id: Date.now(),
                          title: newInitiativeText,
                          completed: false,
                          priority: null,
                        }],
                      };
                    }
                    return task;
                  }),
                };
              }
              return step;
            }),
          };
        }
        return goal;
      });
      setGoals(updatedGoals);
      setNewInitiativeText('');
    }
  };

  const updateGoalTitle = (goalId, newTitle) => {
    const updatedGoals = goals.map(g => 
      g.id === goalId ? {...g, title: newTitle} : g
    );
    setGoals(updatedGoals);
    setEditingItemId(null);
  };

  const updateStepTitle = (stepId, newTitle) => {
    const updatedGoals = goals.map(g => 
      g.id === selectedGoal ? {
        ...g, 
        steps: g.steps.map(s => 
          s.id === stepId ? {...s, title: newTitle} : s
        )
      } : g
    );
    setGoals(updatedGoals);
    setEditingItemId(null);
  };

  const updateTaskTitle = (taskId, newTitle) => {
    const updatedGoals = goals.map(g => 
      g.id === selectedGoal ? {
        ...g, 
        steps: g.steps.map(s => 
          s.id === selectedStep ? {
            ...s,
            tasks: s.tasks.map(t =>
              t.id === taskId ? {...t, title: newTitle} : t
            )
          } : s
        )
      } : g
    );
    setGoals(updatedGoals);
    setEditingItemId(null);
  };

  const updateInitiativeTitle = (initiativeId, newTitle) => {
    const updatedGoals = goals.map(g => 
      g.id === selectedGoal ? {
        ...g, 
        steps: g.steps.map(s => 
          s.id === selectedStep ? {
            ...s,
            tasks: s.tasks.map(t =>
              t.id === selectedTask ? {
                ...t,
                initiatives: (t.initiatives || []).map(i =>
                  i.id === initiativeId ? {...i, title: newTitle} : i
                )
              } : t
            )
          } : s
        )
      } : g
    );
    setGoals(updatedGoals);
    setEditingItemId(null);
  };

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      <Header
        filterState={filterState}
        setFilterState={setFilterState}
        downloadData={downloadData}
        triggerFileUpload={triggerFileUpload}
        copyIndentedData={copyIndentedData}
        clearAllData={clearAllData}
        fileInputRef={fileInputRef}
        handleFileUpload={handleFileUpload}
        logo={logo}
      />

      <div className="flex flex-1 overflow-hidden">
        <GoalsColumn
          goals={goals}
          selectedGoal={selectedGoal}
          setSelectedGoal={setSelectedGoal}
          setSelectedStep={setSelectedStep}
          setSelectedTask={setSelectedTask}
          newGoalText={newGoalText}
          setNewGoalText={setNewGoalText}
          addGoal={addGoal}
          updateGoalTitle={updateGoalTitle}
          setGoals={setGoals}
          columnHeaders={columnHeaders}
          setColumnHeaders={setColumnHeaders}
          openMenuId={openMenuId}
          setOpenMenuId={setOpenMenuId}
          openSubmenuId={openSubmenuId}
          setOpenSubmenuId={setOpenSubmenuId}
          filterState={filterState}
          editingItemId={editingItemId}
          setEditingItemId={setEditingItemId}
          editingText={editingText}
          setEditingText={setEditingText}
          draggedItem={draggedItem}
          handleDragStart={handleDragStart}
          handleDragOver={handleDragOver}
          handleDrop={handleDrop}
        />

        <StepsColumn
          goals={goals}
          selectedGoal={selectedGoal}
          selectedStep={selectedStep}
          setSelectedStep={setSelectedStep}
          setSelectedTask={setSelectedTask}
          newStepText={newStepText}
          setNewStepText={setNewStepText}
          addStep={addStep}
          updateStepTitle={updateStepTitle}
          setGoals={setGoals}
          columnHeaders={columnHeaders}
          setColumnHeaders={setColumnHeaders}
          openMenuId={openMenuId}
          setOpenMenuId={setOpenMenuId}
          openSubmenuId={openSubmenuId}
          setOpenSubmenuId={setOpenSubmenuId}
          filterState={filterState}
          editingItemId={editingItemId}
          setEditingItemId={setEditingItemId}
          editingText={editingText}
          setEditingText={setEditingText}
          draggedItem={draggedItem}
          handleDragStart={handleDragStart}
          handleDragOver={handleDragOver}
          handleDrop={handleDrop}
        />

        <TasksColumn
          goals={goals}
          selectedGoal={selectedGoal}
          selectedStep={selectedStep}
          selectedTask={selectedTask}
          setSelectedTask={setSelectedTask}
          newTaskText={newTaskText}
          setNewTaskText={setNewTaskText}
          addTask={addTask}
          updateTaskTitle={updateTaskTitle}
          setGoals={setGoals}
          columnHeaders={columnHeaders}
          setColumnHeaders={setColumnHeaders}
          openMenuId={openMenuId}
          setOpenMenuId={setOpenMenuId}
          openSubmenuId={openSubmenuId}
          setOpenSubmenuId={setOpenSubmenuId}
          filterState={filterState}
          editingItemId={editingItemId}
          setEditingItemId={setEditingItemId}
          editingText={editingText}
          setEditingText={setEditingText}
          draggedItem={draggedItem}
          handleDragStart={handleDragStart}
          handleDragOver={handleDragOver}
          handleDrop={handleDrop}
        />

        <InitiativesColumn
          goals={goals}
          selectedGoal={selectedGoal}
          selectedStep={selectedStep}
          selectedTask={selectedTask}
          newInitiativeText={newInitiativeText}
          setNewInitiativeText={setNewInitiativeText}
          addInitiative={addInitiative}
          updateInitiativeTitle={updateInitiativeTitle}
          setGoals={setGoals}
          columnHeaders={columnHeaders}
          setColumnHeaders={setColumnHeaders}
          openMenuId={openMenuId}
          setOpenMenuId={setOpenMenuId}
          openSubmenuId={openSubmenuId}
          setOpenSubmenuId={setOpenSubmenuId}
          filterState={filterState}
          editingItemId={editingItemId}
          setEditingItemId={setEditingItemId}
          editingText={editingText}
          setEditingText={setEditingText}
          draggedItem={draggedItem}
          handleDragStart={handleDragStart}
          handleDragOver={handleDragOver}
          handleDrop={handleDrop}
        />
      </div>
    </div>
  );
}